// Resources
import React, { useState } from 'react';
import SidePanel from './SidePanel';

// Icons
import { IoIosArrowDropdown, IoIosArrowDropup } from 'react-icons/io';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { TfiBackRight } from 'react-icons/tfi';
import { FaCheckCircle } from 'react-icons/fa';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import { HiOutlineArrowSmRight } from 'react-icons/hi';

import { NODE_THEMES } from '../Components/NodeTheme';

// Colores de gravedad, alineados con el resto del constructor.
const SEV = {
  error: '#FF2242',
  warn: '#e0a32e',
  ok: '#2fae4e',
};

// Botón redondo del navegador de issues (anterior / siguiente).
const navBtnStyle = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  flexShrink: 0,
  padding: 0,
  border: `1px solid ${theme.border}`,
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: theme.textPrimary,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
});

const ErrorMonitor = ({
  handleErrorMonitoring,
  goToInitialNode,
  goToNode,
  findNodeColor,
  listDisconnectedNodes,
  listDisconnectedOptions,
  listNodesWithErrorInFields,
  triggersDuplicateCount,
  transitionsDuplicateCount,
  animateClass,
  isDark,
}) => {
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const [openSection, setOpenSection] = useState(null);
  const [navIndex, setNavIndex] = useState(-1);

  const toggle = (key) => setOpenSection((prev) => (prev === key ? null : key));

  // Issues que corresponden a un nodo concreto (navegables uno a uno en el
  // canvas). Los duplicados de triggers/transitions son globales, no entran.
  const navigableIssues = [
    ...listDisconnectedNodes,
    ...listDisconnectedOptions,
    ...listNodesWithErrorInFields,
  ];

  const goToIssue = (i) => {
    if (!navigableIssues.length) return;
    const idx = (i + navigableIssues.length) % navigableIssues.length;
    setNavIndex(idx);
    goToNode(navigableIssues[idx]);
  };

  const trigCount = triggersDuplicateCount > 1 ? triggersDuplicateCount : 0;
  const transCount = transitionsDuplicateCount > 1 ? transitionsDuplicateCount : 0;

  // Configuración única que alimenta tanto los tiles como las secciones.
  const sections = [
    {
      key: 'disconnectedNodes',
      tile: 'Desconectados',
      label: 'Nodos desconectados',
      severity: 'warn',
      count: listDisconnectedNodes.length,
      items: listDisconnectedNodes,
      hint: 'Nodos que existen en el flujo pero no están conectados o en uso.',
    },
    {
      key: 'disconnectedOptions',
      tile: 'Botones',
      label: 'Botones sin conexión',
      severity: 'warn',
      count: listDisconnectedOptions.length,
      items: listDisconnectedOptions,
      hint: 'Nodos con botones interactivos sin conexión alguna.',
    },
    {
      key: 'errorFields',
      tile: 'Campos',
      label: 'Nodos con errores',
      severity: 'error',
      count: listNodesWithErrorInFields.length,
      items: listNodesWithErrorInFields,
      hint: 'Nodos con errores internos en sus campos correspondientes.',
    },
    {
      key: 'triggers',
      tile: 'Disparadores',
      label: 'Disparadores duplicados',
      severity: 'error',
      count: trigCount,
      message:
        'Se han encontrado disparadores (triggers) duplicados. Entra a uno de los nodos y presiona GUARDAR para que se ajusten automáticamente.',
    },
    {
      key: 'transitions',
      tile: 'Transiciones',
      label: 'Transiciones duplicadas',
      severity: 'error',
      count: transCount,
      message:
        'Se han encontrado transiciones (transitions) duplicadas. Entra a uno de los nodos y presiona GUARDAR para que se ajusten automáticamente.',
    },
  ];

  const errorActive = sections.some((s) => s.severity === 'error' && s.count > 0);
  const warnActive = sections.some((s) => s.severity === 'warn' && s.count > 0);
  const totalIssues = sections.reduce(
    (acc, s) => acc + (s.items ? s.count : s.count > 0 ? 1 : 0),
    0
  );
  const healthColor = errorActive ? SEV.error : warnActive ? SEV.warn : SEV.ok;
  const healthy = totalIssues === 0;

  const openAndScroll = (key) => {
    setOpenSection(key);
    setTimeout(() => {
      document
        .getElementById(`monitor-section-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
  };

  return (
    <SidePanel title="Monitor de errores" onClose={() => handleErrorMonitoring(true)}>
      {/* Cuerpo */}
      <div
        id="content-list-chats"
        style={{ overflowY: 'auto', padding: '14px' }}
      >
        {/* Cabecera de salud */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '11px 12px',
            borderRadius: '10px',
            border: `1px solid color-mix(in srgb, ${healthColor} 35%, ${theme.panelBg})`,
            backgroundColor: `color-mix(in srgb, ${healthColor} 8%, ${theme.panelBg})`,
            marginBottom: '14px',
          }}
        >
          {healthy ? (
            <FaCheckCircle style={{ fontSize: '20px', color: SEV.ok, flexShrink: 0 }} />
          ) : (
            <BsExclamationTriangleFill
              style={{ fontSize: '18px', color: healthColor, flexShrink: 0 }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme.textPrimary }}>
              {healthy
                ? 'Todo en orden'
                : `${totalIssues} ${totalIssues === 1 ? 'problema' : 'problemas'}`}
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary }}>
              {healthy
                ? 'No se detectaron problemas en el flujo.'
                : 'Revisa los puntos marcados abajo.'}
            </div>
          </div>
        </div>

        {/* Navegador de issues: recorre uno a uno los nodos con problema */}
        {navigableIssues.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '7px 8px 7px 12px',
              borderRadius: '10px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.inputBg,
              marginBottom: '14px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: theme.textPrimary }}>
                {navIndex >= 0
                  ? `Problema ${navIndex + 1} de ${navigableIssues.length}`
                  : `${navigableIssues.length} para revisar`}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {navIndex >= 0
                  ? navigableIssues[navIndex]?.data?.title
                  : 'Recorre los nodos con problema'}
              </div>
            </div>
            <button
              type="button"
              title="Anterior"
              onClick={() => goToIssue(navIndex < 0 ? navigableIssues.length - 1 : navIndex - 1)}
              style={navBtnStyle(theme)}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.panelBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <IoChevronBack style={{ fontSize: '15px' }} />
            </button>
            <button
              type="button"
              title="Siguiente"
              onClick={() => goToIssue(navIndex + 1)}
              style={navBtnStyle(theme)}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.panelBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <IoChevronForward style={{ fontSize: '15px' }} />
            </button>
          </div>
        )}

        {/* Stat-tiles: conteo por categoría, click para abrir su sección */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '14px',
          }}
        >
          {sections.map((s) => {
            const active = s.count > 0;
            const sevColor = SEV[s.severity];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => active && openAndScroll(s.key)}
                style={{
                  flex: '1 1 28%',
                  minWidth: '84px',
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: '10px',
                  border: `1px solid ${active ? `color-mix(in srgb, ${sevColor} 35%, var(--su-surface, #ffffff))` : theme.border}`,
                  backgroundColor: active ? `color-mix(in srgb, ${sevColor} 8%, var(--su-surface, #ffffff))` : theme.inputBg,
                  cursor: active ? 'pointer' : 'default',
                  opacity: active ? 1 : 0.55,
                  transition: 'all 0.15s ease',
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    lineHeight: 1.1,
                    color: active ? sevColor : theme.textTertiary,
                  }}
                >
                  {s.count}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                  {s.tile}
                </div>
              </button>
            );
          })}
        </div>

        {/* Ir al nodo inicial (chip) */}
        <button
          type="button"
          onClick={goToInitialNode}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '100%',
            padding: '8px 14px',
            borderRadius: '8px',
            border: `1px solid color-mix(in srgb, ${SEV.ok} 35%, var(--su-surface, #ffffff))`,
            backgroundColor: `color-mix(in srgb, ${SEV.ok} 12%, var(--su-surface, #ffffff))`,
            color: theme.textPrimary,
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '4px',
          }}
        >
          Ir al nodo inicial
        </button>
        <p style={{ fontSize: 12, color: theme.textTertiary, margin: '0 0 6px 0' }}>
          Busca y enfoca el nodo inicial en todo el flujo.
        </p>

        {/* Estado vacío */}
        {healthy && (
          <div
            style={{
              textAlign: 'center',
              padding: '24px 12px',
              color: theme.textTertiary,
            }}
          >
            <FaCheckCircle style={{ fontSize: '34px', color: SEV.ok }} />
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme.textSecondary, marginTop: '8px' }}>
              Sin problemas detectados
            </div>
            <div style={{ fontSize: '12px', marginTop: '2px' }}>
              Tu flujo está limpio y bien conectado.
            </div>
          </div>
        )}

        {/* Secciones (solo las que tienen algo) */}
        {!healthy &&
          sections
            .filter((s) => s.count > 0)
            .map((s) => {
              const sevColor = SEV[s.severity];
              const isOpen = openSection === s.key;
              return (
                <div
                  key={s.key}
                  id={`monitor-section-${s.key}`}
                  style={{ marginTop: '10px' }}
                >
                  <div
                    onClick={() => toggle(s.key)}
                    className="pointer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 11px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.inputBg,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: sevColor,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: theme.textPrimary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.label}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: sevColor,
                          backgroundColor: `color-mix(in srgb, ${sevColor} 15%, var(--su-surface, #ffffff))`,
                          borderRadius: '999px',
                          padding: '1px 7px',
                          flexShrink: 0,
                        }}
                      >
                        {s.count}
                      </span>
                    </div>
                    {isOpen ? (
                      <IoIosArrowDropup style={{ fontSize: '18px', color: theme.textSecondary }} />
                    ) : (
                      <IoIosArrowDropdown style={{ fontSize: '18px', color: theme.textSecondary }} />
                    )}
                  </div>

                  {isOpen && (
                    <div style={{ padding: '8px 2px 2px 2px' }}>
                      {s.items ? (
                        s.items.map((node) => {
                          const nodeColor = findNodeColor(node.type);
                          return (
                            <button
                              key={node.id}
                              type="button"
                              onClick={() => goToNode(node)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 10px',
                                marginBottom: '6px',
                                borderRadius: '8px',
                                border: `1px solid ${theme.border}`,
                                borderLeft: `3px solid ${nodeColor}`,
                                backgroundColor: theme.panelBg,
                                color: theme.textPrimary,
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s ease',
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor = theme.inputBg)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = theme.panelBg)
                              }
                            >
                              <span
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {node.data.title}
                              </span>
                              <HiOutlineArrowSmRight
                                style={{ fontSize: '16px', color: theme.textTertiary, flexShrink: 0 }}
                              />
                            </button>
                          );
                        })
                      ) : (
                        <p style={{ fontSize: 12, color: theme.textSecondary, margin: 0, lineHeight: 1.5 }}>
                          {s.message}
                        </p>
                      )}
                    </div>
                  )}
                  {s.hint && (
                    <p style={{ fontSize: 12, color: theme.textTertiary, margin: '4px 2px 0 2px' }}>
                      {s.hint}
                    </p>
                  )}
                </div>
              );
            })}
      </div>
    </SidePanel>
  );
};

export default ErrorMonitor;
